/**
 * Web Speech API Service Implementation
 *
 * Uses the browser's native SpeechRecognition API for speech-to-text.
 * Best supported in Chrome with excellent Greek language support.
 */

import {
  ISpeechRecognitionService,
  TranscriptResult,
  SpeechRecognitionError,
  SpeechRecognitionConfig,
  RecognitionStatus,
  OnResultCallback,
  OnErrorCallback,
  OnStatusChangeCallback,
  DEFAULT_CONFIG,
} from './types';

/**
 * Map Web Speech API error codes to our error format
 */
const mapErrorCode = (code: string): SpeechRecognitionError => {
  const errorMap: Record<string, SpeechRecognitionError> = {
    'no-speech': {
      code: 'NO_SPEECH',
      message: 'No speech was detected. Please try speaking again.',
      recoverable: true,
    },
    'aborted': {
      code: 'ABORTED',
      message: 'Speech recognition was aborted.',
      recoverable: true,
    },
    'audio-capture': {
      code: 'AUDIO_CAPTURE',
      message: 'No microphone was found or microphone access was denied.',
      recoverable: false,
    },
    'network': {
      code: 'NETWORK',
      message: 'Network error occurred during speech recognition.',
      recoverable: true,
    },
    'not-allowed': {
      code: 'NOT_ALLOWED',
      message: 'Microphone permission was denied. Please allow microphone access.',
      recoverable: false,
    },
    'service-not-allowed': {
      code: 'SERVICE_NOT_ALLOWED',
      message: 'Speech recognition service is not allowed.',
      recoverable: false,
    },
    'bad-grammar': {
      code: 'BAD_GRAMMAR',
      message: 'Speech grammar error.',
      recoverable: true,
    },
    'language-not-supported': {
      code: 'LANGUAGE_NOT_SUPPORTED',
      message: 'The selected language is not supported.',
      recoverable: false,
    },
  };

  return errorMap[code] || {
    code: 'UNKNOWN',
    message: `Unknown error: ${code}`,
    recoverable: true,
  };
};

export class WebSpeechService implements ISpeechRecognitionService {
  private recognition: SpeechRecognition | null = null;
  private config: Required<SpeechRecognitionConfig>;
  private status: RecognitionStatus = RecognitionStatus.IDLE;

  private resultCallback: OnResultCallback | null = null;
  private errorCallback: OnErrorCallback | null = null;
  private statusChangeCallback: OnStatusChangeCallback | null = null;

  private restartAttempts = 0;
  private maxRestartAttempts = 3;
  private restartTimeout: NodeJS.Timeout | null = null;
  // Set when a language change interrupts an active session; consumed in
  // handleEnd, which restarts only after the browser session has fully ended.
  private pendingRestart = false;

  constructor(config: SpeechRecognitionConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.initializeRecognition();
  }

  /**
   * Initialize the SpeechRecognition instance
   */
  private initializeRecognition(): void {
    if (!this.isSupported()) {
      console.warn('[WebSpeechService] Speech recognition is not supported in this browser');
      return;
    }

    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognitionAPI();

    // Configure recognition
    this.recognition.continuous = this.config.continuous;
    this.recognition.interimResults = this.config.interimResults;
    this.recognition.lang = this.config.language;
    this.recognition.maxAlternatives = this.config.maxAlternatives;

    // Set up event handlers
    this.recognition.onstart = this.handleStart.bind(this);
    this.recognition.onend = this.handleEnd.bind(this);
    this.recognition.onresult = this.handleResult.bind(this);
    this.recognition.onerror = this.handleError.bind(this);
    this.recognition.onspeechstart = this.handleSpeechStart.bind(this);
    this.recognition.onspeechend = this.handleSpeechEnd.bind(this);

    console.log('[WebSpeechService] Initialized with config:', this.config);
  }

  /**
   * Handle recognition start
   */
  private handleStart(): void {
    console.log('[WebSpeechService] Recognition started');
    this.restartAttempts = 0;
    this.setStatus(RecognitionStatus.LISTENING);
  }

  /**
   * Handle recognition end
   */
  private handleEnd(): void {
    console.log('[WebSpeechService] Recognition ended');

    // A language change requested a restart: the old session has now fully
    // ended, so starting here cannot race it (start() applies the new language).
    if (this.pendingRestart) {
      this.pendingRestart = false;
      this.start();
      return;
    }

    // If we were listening and didn't explicitly stop, try to restart
    if (this.status === RecognitionStatus.LISTENING) {
      this.attemptRestart();
    } else if (this.status !== RecognitionStatus.ERROR) {
      // Don't clobber a fatal ERROR status with IDLE — consumers key off it
      this.setStatus(RecognitionStatus.IDLE);
    }
  }

  /**
   * Handle speech recognition results
   */
  private handleResult(event: SpeechRecognitionEvent): void {
    if (!this.resultCallback) {
      return;
    }

    // One event can carry several new/updated results (event.resultIndex ..
    // results.length - 1), e.g. a newly-finalized sentence plus a fresh
    // interim. Emit every final individually so none is lost, then the
    // combined text of the remaining interims.
    const timestamp = Date.now();
    let interimText = '';

    for (let i = event.resultIndex ?? 0; i < event.results.length; i++) {
      const result = event.results[i];
      const alternative = result[0];

      if (result.isFinal) {
        this.resultCallback({
          text: alternative.transcript,
          isFinal: true,
          confidence: alternative.confidence,
          language: this.config.language,
          timestamp,
        });
      } else {
        interimText += alternative.transcript;
      }
    }

    if (interimText) {
      this.resultCallback({
        text: interimText,
        isFinal: false,
        language: this.config.language,
        timestamp,
      });
    }
  }

  /**
   * Handle recognition errors
   */
  private handleError(event: SpeechRecognitionErrorEvent): void {
    const error = mapErrorCode(event.error);
    console.error('[WebSpeechService] Error:', error);

    if (this.errorCallback) {
      this.errorCallback(error);
    }

    // Recoverable errors are followed by the browser's onend event while
    // status is still LISTENING, which triggers the restart in handleEnd —
    // restarting here as well would double-count restartAttempts.
    if (!error.recoverable) {
      this.setStatus(RecognitionStatus.ERROR);
    }
  }

  /**
   * Handle speech start detection
   */
  private handleSpeechStart(): void {
    console.log('[WebSpeechService] Speech detected');
  }

  /**
   * Handle speech end detection
   */
  private handleSpeechEnd(): void {
    console.log('[WebSpeechService] Speech ended');
  }

  /**
   * Attempt to restart recognition after unexpected end
   */
  private attemptRestart(): void {
    if (this.restartAttempts >= this.maxRestartAttempts) {
      console.warn('[WebSpeechService] Max restart attempts reached');
      if (this.restartTimeout) {
        clearTimeout(this.restartTimeout);
        this.restartTimeout = null;
      }
      this.setStatus(RecognitionStatus.ERROR);
      if (this.errorCallback) {
        this.errorCallback({
          code: 'MAX_RESTARTS',
          message: 'Speech recognition stopped unexpectedly and could not be restarted.',
          recoverable: false,
        });
      }
      return;
    }

    this.restartAttempts++;
    console.log(`[WebSpeechService] Attempting restart (${this.restartAttempts}/${this.maxRestartAttempts})`);

    // Clear any existing timeout
    if (this.restartTimeout) {
      clearTimeout(this.restartTimeout);
    }

    // Restart after a short delay
    this.restartTimeout = setTimeout(() => {
      this.restartTimeout = null;
      if (this.recognition && this.status === RecognitionStatus.LISTENING) {
        try {
          this.recognition.start();
        } catch (e) {
          // start() can throw (e.g. InvalidStateError while the old session
          // is still winding down) — retry within the attempt budget instead
          // of leaving status stuck at LISTENING with no recognition running.
          console.error('[WebSpeechService] Restart failed:', e);
          this.attemptRestart();
        }
      }
    }, 100);
  }

  /**
   * Update status and notify callback
   */
  private setStatus(status: RecognitionStatus): void {
    if (this.status !== status) {
      this.status = status;
      console.log('[WebSpeechService] Status changed:', status);
      if (this.statusChangeCallback) {
        this.statusChangeCallback(status);
      }
    }
  }

  // ISpeechRecognitionService implementation

  isSupported(): boolean {
    return typeof window !== 'undefined' &&
      ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
  }

  isListening(): boolean {
    return this.status === RecognitionStatus.LISTENING;
  }

  getStatus(): RecognitionStatus {
    return this.status;
  }

  start(): void {
    if (!this.recognition) {
      console.error('[WebSpeechService] Recognition not initialized');
      return;
    }

    if (this.status === RecognitionStatus.LISTENING) {
      console.warn('[WebSpeechService] Already listening');
      return;
    }

    console.log('[WebSpeechService] Starting recognition');
    this.setStatus(RecognitionStatus.STARTING);
    this.restartAttempts = 0;

    // Sessions always begin with the currently configured language
    this.recognition.lang = this.config.language;

    try {
      this.recognition.start();
    } catch (e) {
      console.error('[WebSpeechService] Failed to start:', e);
      this.setStatus(RecognitionStatus.ERROR);
      if (this.errorCallback) {
        this.errorCallback({
          code: 'START_FAILED',
          message: 'Failed to start speech recognition.',
          recoverable: true,
        });
      }
    }
  }

  stop(): void {
    if (!this.recognition) {
      return;
    }

    console.log('[WebSpeechService] Stopping recognition');
    this.setStatus(RecognitionStatus.STOPPING);
    this.pendingRestart = false;

    // Clear restart timeout
    if (this.restartTimeout) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }

    try {
      this.recognition.stop();
    } catch (e) {
      console.error('[WebSpeechService] Failed to stop:', e);
    }

    this.setStatus(RecognitionStatus.IDLE);
  }

  onResult(callback: OnResultCallback): void {
    this.resultCallback = callback;
  }

  onError(callback: OnErrorCallback): void {
    this.errorCallback = callback;
  }

  onStatusChange(callback: OnStatusChangeCallback): void {
    this.statusChangeCallback = callback;
  }

  setLanguage(language: string): void {
    this.config.language = language;

    if (!this.recognition) {
      console.warn('[WebSpeechService] Cannot set language - recognition not initialized');
      return;
    }

    const isActive =
      this.status === RecognitionStatus.LISTENING ||
      this.status === RecognitionStatus.STARTING;

    if (isActive) {
      // The in-flight session keeps the language it started with, and
      // recognition.stop() only takes effect when onend fires — so restart
      // from handleEnd rather than calling start() here, which would race
      // the still-ending session and throw InvalidStateError. The flag is
      // set after stop(), which clears it (a manual stop must win).
      this.stop();
      this.pendingRestart = true;
    } else {
      this.recognition.lang = language;
    }

    console.log('[WebSpeechService] Language updated to:', language);
  }

  destroy(): void {
    console.log('[WebSpeechService] Destroying service');
    // Null the callbacks before stop(): stop() fires status changes, and
    // consumers must not be called back during or after teardown.
    this.resultCallback = null;
    this.errorCallback = null;
    this.statusChangeCallback = null;
    this.pendingRestart = false;
    this.stop();
    this.recognition = null;
  }
}
